import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/commercial-foam-roofing";
const NAME = "Commercial Foam Roofing";
const TITLE = "Commercial Foam Roofing in Phoenix | Durafoam";
const DESCRIPTION =
  "Commercial foam roofing in Phoenix since 1989. New SPF roofs, foam reroofs, and foam-over conversions on warehouses, retail, office, and multifamily buildings.";

const faqs = [
  {
    q: "What is a commercial foam roof?",
    a: "A commercial foam roof — technically SPF, spray polyurethane foam — is applied as a two-part liquid that expands into a seamless, closed-cell insulation layer bonded directly to the deck. A reflective elastomeric top coat is then sprayed over the foam for UV protection and waterproofing. The result is a fully monolithic roof with no seams, self-flashing at every penetration, and R-6.5 per inch of insulation.",
  },
  {
    q: "Why is foam the best commercial roof for Phoenix?",
    a: "Three reasons: (1) seamless coverage over the many HVAC curbs, drains, and conduit penetrations a Phoenix commercial roof carries, (2) the ability to be sprayed thicker in low spots to re-slope ponding areas without tear-off, and (3) it's the only commercial system that's genuinely renewable — recoat every 5 years and the foam substrate underneath keeps going indefinitely.",
  },
  {
    q: "How thick is a commercial foam roof?",
    a: "Standard commercial installs run 1.5–2 inches of foam, though we'll go thicker in low spots to correct ponding or wherever additional R-value pencils out against energy bills. A 2-inch install delivers roughly R-13 — meaningfully higher than any single-ply membrane can offer without a separate insulation board.",
  },
  {
    q: "Can you install foam over an existing TPO, mod-bit, or BUR roof?",
    a: "Almost always, yes. A foam-over reroof is one of the biggest advantages of the system: instead of tearing off the existing membrane (dumpsters, disposal fees, exposure to weather), we clean and prep the existing roof and spray directly over it. This is standard practice for converting aging TPO, modified bitumen, and BUR commercial roofs to foam.",
  },
  {
    q: "How long does a commercial foam roof last?",
    a: "A properly installed and maintained SPF roof in Phoenix routinely lasts 30+ years. The foam substrate is essentially permanent as long as the top coat stays intact — recoat every 5 years and you avoid the tear-off/replacement cycle single-ply systems require every 15–20 years.",
  },
  {
    q: "How much does a commercial foam roof cost in Phoenix?",
    a: "Full commercial SPF installs typically run $6–$9 per square foot depending on building size, thickness, and access. Recoats on existing foam run $2.50–$4.50 per square foot. See our commercial roofing cost guide for a full breakdown.",
  },
  {
    q: "Can you install a foam roof while my business stays open?",
    a: "Yes. Foam installs are relatively fast and low-disruption compared to tear-off replacements. We schedule around your operating hours, notify tenants ahead of any odor-producing phases, and stage the roof to keep loading docks and customer access clear.",
  },
  {
    q: "Does a foam roof qualify for cool-roof rebates?",
    a: "Yes — a properly coated SPF roof easily meets the SRI thresholds most Phoenix-metro utility cool-roof rebate programs require. We document the specs at proposal time so you can submit the paperwork.",
  },
  {
    q: "Is foam roofing good for commercial buildings?",
    a: "It's the system we recommend on most low-slope commercial buildings in Phoenix. Commercial roofs carry dense HVAC curbs, drains, and conduit runs — exactly the penetrations that single-ply roofs fail around — and foam self-flashes all of them with no seams. It also adds real R-value over occupied space and can be sprayed thicker to re-slope ponding areas without structural work.",
  },
  {
    q: "How long does a commercial foam roof take to install?",
    a: "Most commercial foam-over reroofs run one to three weeks depending on square footage, deck prep, and how much of the roof we can stage at once. Prep is usually the longest phase; spray and coating move fast. Buildings stay open throughout — we phase the work, keep docks and entrances clear, and schedule the odor-producing spray phase around your operations.",
  },
  {
    q: "Do you offer a commercial roof maintenance program?",
    a: "Yes. It covers an annual photo-documented inspection, scheduled recoats about every 5 years, minor repairs handled during the visit, and a written report you can hand to ownership, a board, a lender, or a buyer. It also keeps the manufacturer warranty in force, since those warranties require documented maintenance.",
  },
  {
    q: "What does a commercial foam roof cost to own over 30 years?",
    a: "Less than single-ply in almost every case we've priced out, because foam is never thrown away. You install once and then re-buy only the coating every 5 years, while a single-ply roof typically needs a full tear-off and replacement around year 15–20 and often again before year 30 — with disposal, dumpsters, and tenant disruption each time. Actual numbers depend on your deck, drains, HVAC density, and access, so we put per-square-foot ranges in writing after a walk.",
  },
];

export const Route = createFileRoute("/commercial-foam-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="commercial-foam-roofing"
      extraSections={SEARCH_TERM_SECTIONS["commercial-foam-roofing"]}
      eyebrow="Commercial"
      h1="Commercial Foam Roofing in Phoenix, AZ"
      subhead="Phoenix commercial spray polyurethane foam specialists since 1989 — seamless, self-flashing, R-6.5 per inch, and genuinely renewable through recoats. New installs, full reroofs, and foam-over conversions on TPO, modified bitumen, and BUR commercial roofs."
      heroBullets={[
        "SPF installs on warehouses, retail, office & multifamily",
        "Foam-over conversions on TPO, mod-bit, and BUR",
        "Re-slope ponding areas — no tear-off required",
        "Operate-during-work scheduling for commercial tenants",
      ]}
      whatIs={{
        heading: "What is commercial foam roofing?",
        body: (
          <>
            <p>
              Commercial foam roofing — spray polyurethane foam, or SPF — is applied as a two-part
              liquid that expands into a fully monolithic, closed-cell layer bonded directly to
              your deck. A reflective elastomeric top coat is then sprayed over the foam for UV
              protection and waterproofing. The finished roof has no seams, self-flashes at every
              penetration, and adds real insulation value — R-6.5 per inch — where Phoenix
              commercial buildings need it most.
            </p>
            <p>
              For low-slope commercial buildings across the Valley, SPF is our primary reroof
              recommendation. It's the system we install on new commercial construction and the
              system we recommend converting to when an aging TPO, modified bitumen, or built-up
              roof reaches end of life.
            </p>
            <h3 className="mt-8 text-xl font-bold text-primary">Why foam wins on Phoenix commercial roofs</h3>
            <ul>
              <li>
                <strong>Seamless coverage.</strong> One continuous surface over the entire roof —
                no seams, no laps, no field-welded terminations to fail. Every HVAC curb, drain,
                conduit run, and parapet is self-flashed by the foam itself.
              </li>
              <li>
                <strong>Ponding correction without tear-off.</strong> Foam can be sprayed thicker
                in low spots to re-slope water toward existing drains. No membrane system can do
                this — with TPO or mod-bit, ponding is either accepted or fixed with expensive
                structural work.
              </li>
              <li>
                <strong>Real R-value.</strong> R-6.5 per inch of foam. A 2-inch install adds
                roughly R-13 — meaningful in a market where rooftop HVAC runs 8+ months a year.
              </li>
              <li>
                <strong>Genuinely renewable.</strong> Recoat every 5 years and the foam
                substrate keeps going indefinitely. This is the single biggest economic advantage
                over single-ply — no replacement cycle, no landfill trips.
              </li>
              <li>
                <strong>Reflective from day one.</strong> White elastomeric top coat delivers high
                SRI and qualifies for most Phoenix-metro utility cool-roof rebate programs.
              </li>
            </ul>
          </>
        ),
      }}
      climate={{
        heading: "Commercial foam roofing considerations for Phoenix",
        body: (
          <>
            <p>
              Phoenix is one of the best foam-roofing climates in North America. Dry air, minimal
              freeze-thaw, and long spray-window seasons let us install foam at consistent
              quality year-round. What matters most on a Phoenix commercial foam job:
            </p>
            <ul>
              <li>
                <strong>UV protection is everything.</strong> Foam itself doesn't like UV — the
                elastomeric top coat is what makes the system durable in Phoenix. We spec coating
                thickness for the sun exposure your roof actually gets.
              </li>
              <li>
                <strong>Monsoon detailing.</strong> Wind-driven rain finds every weakness. We
                spend extra time on parapet caps, scupper transitions, and rooftop equipment
                curbs — the places single-ply roofs typically fail first.
              </li>
              <li>
                <strong>HVAC-heavy roofs.</strong> Foam wraps every curb, gas line, condensate
                drain, and conduit stub continuously. Compare that to TPO, where each penetration
                is a hand-flashed detail that has to be inspected annually.
              </li>
              <li>
                <strong>Cool-roof rebates.</strong> APS, SRP, and several municipal programs offer
                rebates for reflective roofs meeting SRI thresholds. A properly coated SPF roof
                easily qualifies; we document specs at proposal time.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our commercial foam roofing process",
        steps: [
          {
            title: "Roof assessment & core sampling",
            body: "Senior estimator walks the roof, measures, photo-documents penetrations and problem areas, and pulls core samples on existing single-ply or BUR roofs to confirm moisture status before we quote a foam-over.",
          },
          {
            title: "Written proposal with system spec",
            body: "Foam thickness, coating mils, manufacturer, warranty terms, staging plan, and schedule. When a foam-over is possible we quote it alongside tear-off pricing so you see the real trade-off.",
          },
          {
            title: "Prep & spray with operations coordination",
            body: "Existing roof is cleaned, prepped, and any wet insulation removed. Foam is sprayed in monitored lifts to spec thickness. Base coat, granules where needed, and finish coat applied per manufacturer requirements. Daily progress updates to your facilities team.",
          },
          {
            title: "Warranty & recoat schedule",
            body: "Manufacturer warranty plus our workmanship warranty. We schedule your first recoat inspection at year 7–10 so you're never surprised by a coating failure. Optional maintenance program keeps the warranty in force across its full life.",
          },
        ],
      }}
      whyChoose={{
        heading: "Foam vs. TPO vs. mod-bit for a Phoenix commercial roof",
        body: (
          <>
            <p>
              Every commercial building owner in Phoenix eventually hears pitches for TPO,
              modified bitumen, or a foam reroof. Here's the honest comparison:
            </p>
            <ul>
              <li>
                <strong>Foam (SPF):</strong> Seamless, self-flashing, re-slopable, R-6.5/inch, and
                renewable through recoats. Best long-term economics on almost every low-slope
                Phoenix commercial building. Our recommendation.
              </li>
              <li>
                <strong>TPO:</strong> Well-engineered single-ply that works when installed
                correctly. Every seam is a potential failure point, every penetration requires
                hand-flashing, and end of life means full tear-off. When your TPO fails, we
                usually recommend a foam-over rather than a second TPO install.
              </li>
              <li>
                <strong>Modified bitumen:</strong> Torch-applied asphalt sheet. Heavy, seam-prone,
                and reaches end of life around 15–20 years in Phoenix sun. When mod-bit gives up,
                foam-over is almost always the better economic call than another mod-bit tear-off.
              </li>
            </ul>
            <p>
              For a written recommendation on your specific building,{" "}
              <Link
                to="/free-roof-inspection-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                request a free commercial roof inspection
              </Link>{" "}
              or see our{" "}
              <Link
                to="/commercial-roofing-cost-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                commercial roofing cost guide
              </Link>{" "}
              for real Phoenix numbers.
            </p>
          </>
        ),
      }}
      extraSection={{
        heading: "Commercial foam roofing by building type",
        body: (
          <>
            <ul>
              <li>
                <strong>Warehouses & distribution centers.</strong> Foam is the near-default
                recommendation — large low-slope roofs with dense HVAC and drain networks are
                exactly what SPF is best at. See{" "}
                <Link
                  to="/warehouse-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  warehouse roofing
                </Link>{" "}
                for our full warehouse process.
              </li>
              <li>
                <strong>Retail & strip centers.</strong> Multi-tenant scheduling and
                storefront-visible staging. We spray in phases and never block customer access.
              </li>
              <li>
                <strong>Office buildings.</strong> Class-A appearance requirements met with clean
                coating detailing at parapets and scuppers. Reflective foam noticeably reduces
                mechanical load on tenant HVAC.
              </li>
              <li>
                <strong>Multifamily & HOA.</strong> See{" "}
                <Link
                  to="/apartment-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  apartment & condo roofing
                </Link>{" "}
                and{" "}
                <Link
                  to="/hoa-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  HOA roofing
                </Link>{" "}
                for how we handle board decisions and phased community-wide recoat programs.
              </li>
              <li>
                <strong>Churches, restaurants, and specialty.</strong> Weekend or off-hour
                scheduling and detailed penetration work around grease exhaust, HVAC, and roof
                access — the details that make or break a specialty commercial roof.
              </li>
            </ul>
            <p>
              For everything above the low-slope layer — tile and shingle installs on pitched
              commercial buildings, or repair-only service on aging TPO/mod-bit/BUR — see{" "}
              <Link
                to="/commercial-roofing"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                our full commercial roofing services
              </Link>
              .
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Ready for a foam roof or a foam-over reroof?"
      ctaBody="Call us or request a free commercial inspection. We'll walk the roof, pull cores where needed, and hand you a written recommendation with foam-over vs. tear-off pricing side by side — no pressure, no games."
    />
  );
}
